import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import bg3 from "../assets/images/solar/bg3.webp"  // Smart & Green Real Estate
import bg2 from "../assets/images/solar/bg5.webp"  // E-Mobility Services
import bg1 from "../assets/images/solar/bg1.webp"  // Photovoltaic Solar Energy

import Navbar from "../components/navbar";
import About from "../components/about";
import Services from "../components/services";
import AgencyTab from "../components/agencyTab";
import Cta from "../components/cta";
import Client from "../components/client";
import Pricing from "../components/pricing";
import Blogs from "../components/blog";
import GetInTouch from "../components/getInTuoch";
import Footer from "../components/footer";
import Switcher from "../components/switcher";

export default function Index(){
    const [currentSlide, setCurrentSlide] = useState(0);

    // Hero slides with service-specific content matched to backgrounds
    const heroSlides = [
        {
            image: bg1,  // bg3 for Solar Energy
            title: "Powering Rwanda with Clean Solar Energy",
            description: "Leading provider of photovoltaic solar solutions. From residential rooftops to large-scale commercial installations, we deliver reliable off-grid and on-grid solar systems that reduce costs and carbon footprint.",
            service: "Photovoltaic Solar Energy",
            icon: "☀️"
        },
        {
            image: bg3,  // bg1 for Smart & Green Real Estate
            title: "Building Tomorrow's Green Communities",
            description: "Innovative smart and green real estate development in Rwanda. We design and construct eco-friendly buildings with integrated renewable energy, sustainable materials, and intelligent energy management systems.",
            service: "Smart & Green Real Estate",
            icon: "🏢"
        },
        {
            image: bg2,  // bg2 for E-Mobility Services
            title: "Driving the Electric Revolution",
            description: "Comprehensive e-mobility solutions for Rwanda's sustainable transport future. EV charging infrastructure, fleet electrification, and complete electric vehicle services to accelerate the transition to clean transportation.",
            service: "E-Mobility Services",
            icon: "⚡"
        }
    ];

    // Auto-slide effect
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6000);

        return () => clearInterval(interval);
    }, []);

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    return(
        <>
        <Navbar/>
        
        <section className="relative h-screen overflow-hidden" id="home">
            {/* Background slides with smooth transitions */}
            {heroSlides.map((slide, index) => (
                <div
                    key={index}
                    className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out ${
                        currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                    style={{ backgroundImage: `url(${slide.image})` }}
                >
                    {/* Enhanced gradient overlay for better text contrast */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/60"></div>
                </div>
            ))}

            {/* Centered Content Container - FIXED VERTICAL CENTERING */}
            <div className="container relative z-20 h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-4xl mx-auto">
                    {/* Single container for all slides with proper centering */}
                    <div className="relative w-full h-96 flex items-center justify-center">
                        {heroSlides.map((slide, index) => (
                            <div
                                key={index}
                                className={`absolute w-full max-w-4xl text-center lg:text-left space-y-4 sm:space-y-5 md:space-y-6 transition-all duration-1000 ease-in-out ${
                                    currentSlide === index 
                                        ? 'opacity-100 translate-y-0' 
                                        : 'opacity-0 translate-y-8 pointer-events-none'
                                }`}
                            >
                                {/* Service badge with enhanced styling */}
                                <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 bg-teal-500/25 backdrop-blur-md border border-teal-400/40 rounded-full shadow-lg">
                                    <span className="text-xl sm:text-2xl">{slide.icon}</span>
                                    <span className="text-teal-300 font-semibold text-sm sm:text-base tracking-wide">
                                        {slide.service}
                                    </span>
                                </div>

                                {/* Title with improved typography */}
                                <h1 className="font-bold text-white leading-tight tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[58px]">
                                    {slide.title}
                                </h1>

                                {/* Description with better readability */}
                                <p className="text-slate-100 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto lg:mx-0 leading-relaxed sm:leading-loose">
                                    {slide.description}
                                </p>

                                {/* Enhanced CTA button */}
                                <div className="pt-4 sm:pt-6">
                                    <Link 
                                        to="/"
                                        className="inline-flex items-center justify-center h-12 sm:h-14 px-8 sm:px-10 tracking-wide font-semibold rounded-xl bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white shadow-2xl shadow-teal-500/25 transition-all duration-300 hover:shadow-2xl hover:shadow-teal-500/40 hover:scale-105 text-base sm:text-lg"
                                    >
                                        Smart Solutions
                                        <i className="mdi mdi-chevron-right ms-2 text-xl"></i>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Enhanced slide navigation dots */}
            <div className="absolute bottom-8 sm:bottom-10 left-1/2 -translate-x-1/2 z-30 flex gap-3 sm:gap-4">
                {heroSlides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`transition-all duration-500 ease-out rounded-full shadow-lg ${
                            currentSlide === index 
                                ? 'w-10 sm:w-14 h-3 bg-gradient-to-r from-teal-500 to-emerald-500 shadow-teal-500/50' 
                                : 'w-3 h-3 bg-white/50 hover:bg-white/80 hover:scale-110'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>

            {/* Optional: Progress bar for auto-slide indicator */}
            <div className="absolute top-0 left-0 right-0 h-1 z-30 bg-white/10">
                <div 
                    className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-6000 ease-linear"
                    style={{ 
                        width: currentSlide === 0 ? '33.33%' : currentSlide === 1 ? '66.66%' : '100%' 
                    }}
                />
            </div>
        </section>

        <About/>
        <AgencyTab/>
        <Services/>
        <Blogs/>
        <Cta/>
        <GetInTouch/>
        {/* <Pricing/> */}
        <Footer/>
        <Switcher/>
        </>
    )
}